/**
 * Trends Analytics API Service
 *
 * Maneja todas las llamadas a la API de análisis de tendencias.
 */

import axios from 'axios';

// Create a separate axios instance for trends API
// Trends are served at /api/trends/, not /api/content/trends/
const TRENDS_API_URL = process.env.REACT_APP_API_URL
  ? process.env.REACT_APP_API_URL.replace('/content', '') + '/trends'
  : 'http://localhost:8000/api/trends';

const trendsApi = axios.create({
  baseURL: TRENDS_API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Función para obtener CSRF token de las cookies
function getCookie(name) {
  let cookieValue = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === (name + '=')) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
}

// Añadir CSRF token a peticiones POST/PUT/DELETE
trendsApi.interceptors.request.use((config) => {
  if (['post', 'put', 'patch', 'delete'].includes(config.method)) {
    config.headers['X-CSRFToken'] = getCookie('csrftoken');
  }
  return config;
});

// Add interceptors for token handling
trendsApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Token ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

const trendsService = {
  /**
   * Obtener dashboard completo de tendencias
   * @param {string} platform - 'instagram' o 'tiktok'
   * @returns {Promise} Dashboard data
   */
  getDashboard: async (platform = 'instagram') => {
    try {
      const response = await trendsApi.get('/recommendations/dashboard/', {
        params: { platform }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching trends dashboard:', error);
      throw error;
    }
  },

  /**
   * Obtener lista de recomendaciones
   * @param {Object} filters - Filtros opcionales
   * @returns {Promise} Lista de recomendaciones
   */
  getRecommendations: async (filters = {}) => {
    try {
      const response = await trendsApi.get('/recommendations/', {
        params: filters
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching recommendations:', error);
      throw error;
    }
  },

  /**
   * Obtener top 5 tendencias para widget
   * @param {string} platform - 'instagram' o 'tiktok'
   * @returns {Promise} Top trends
   */
  getTopTrends: async (platform = 'instagram') => {
    try {
      const response = await trendsApi.get('/recommendations/top_trends/', {
        params: { platform }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching top trends:', error);
      throw error;
    }
  },

  /**
   * Regenerar todas las recomendaciones
   * @param {string} platform - 'instagram', 'tiktok', o null para ambas
   * @param {number} days - Días de análisis (default: 30)
   * @returns {Promise} Resultado de regeneración
   */
  refreshRecommendations: async (platform = null, days = 30) => {
    try {
      const data = { days };
      if (platform) {
        data.platform = platform;
      }

      const response = await trendsApi.post('/recommendations/refresh/', data);
      return response.data;
    } catch (error) {
      console.error('Error refreshing recommendations:', error);
      throw error;
    }
  },

  /**
   * Generar AI Insights bajo demanda
   * @param {string} platform - 'instagram' o 'tiktok'
   * @returns {Promise} Insights generados
   */
  generateAIInsights: async (platform = 'instagram') => {
    try {
      const response = await trendsApi.post('/recommendations/generate_ai_insights/', {
        platform
      });
      return response.data;
    } catch (error) {
      console.error('Error generating AI insights:', error);
      throw error;
    }
  },

  /**
   * Obtener hashtags trending
   * @param {string} platform - 'instagram' o 'tiktok'
   * @returns {Promise} Lista de hashtags
   */
  getHashtags: async (platform = 'instagram') => {
    try {
      const response = await trendsApi.get('/hashtags/', {
        params: { platform }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching hashtags:', error);
      throw error;
    }
  },

  /**
   * Obtener horarios recomendados
   * @param {string} platform - 'instagram' o 'tiktok'
   * @returns {Promise} Lista de horarios
   */
  getPostingTimes: async (platform = 'instagram') => {
    try {
      const response = await trendsApi.get('/posting-times/', {
        params: { platform }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching posting times:', error);
      throw error;
    }
  },

  /**
   * Obtener formatos de contenido
   * @param {string} platform - 'instagram' o 'tiktok'
   * @returns {Promise} Lista de formatos
   */
  getContentFormats: async (platform = 'instagram') => {
    try {
      const response = await trendsApi.get('/content-formats/', {
        params: { platform }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching content formats:', error);
      throw error;
    }
  },

  /**
   * Obtener insights de IA
   * @param {string} insightType - Tipo de insight (opcional)
   * @returns {Promise} Insights de IA
   */
  getAIInsights: async (insightType = null) => {
    try {
      const params = insightType ? { insight_type: insightType } : {};
      const response = await trendsApi.get('/ai-insights/', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching AI insights:', error);
      throw error;
    }
  },

  /**
   * Obtener snapshots de Google Trends
   * @param {string} keyword - Keyword específico (opcional)
   * @returns {Promise} Snapshots de Google Trends
   */
  getGoogleTrends: async (keyword = null) => {
    try {
      const params = keyword ? { keyword } : {};
      const response = await trendsApi.get('/google-trends/', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching Google trends:', error);
      throw error;
    }
  },

  /**
   * Obtener categorías de tendencias
   * @returns {Promise} Lista de categorías
   */
  getCategories: async () => {
    try {
      const response = await trendsApi.get('/categories/');
      return response.data;
    } catch (error) {
      console.error('Error fetching categories:', error);
      throw error;
    }
  },

  /**
   * Crear nueva categoría de tendencias
   * @param {Object} data - Datos de la categoría
   * @returns {Promise} Categoría creada
   */
  createCategory: async (data) => {
    try {
      const response = await trendsApi.post('/categories/', data);
      return response.data;
    } catch (error) {
      console.error('Error creating category:', error);
      throw error;
    }
  },

  /**
   * Actualizar categoría
   * @param {number} id - ID de la categoría
   * @param {Object} data - Datos actualizados
   * @returns {Promise} Categoría actualizada
   */
  updateCategory: async (id, data) => {
    try {
      const response = await trendsApi.put(`/categories/${id}/`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating category:', error);
      throw error;
    }
  },

  /**
   * Eliminar categoría
   * @param {number} id - ID de la categoría
   * @returns {Promise} Confirmación
   */
  deleteCategory: async (id) => {
    try {
      await trendsApi.delete(`/categories/${id}/`);
      return { success: true };
    } catch (error) {
      console.error('Error deleting category:', error);
      throw error;
    }
  },

  /**
   * Obtener categorías oficiales de Google Trends
   * @returns {Promise} Lista de categorías de Google Trends
   */
  getGoogleTrendsCategories: async () => {
    try {
      const response = await trendsApi.get('/categories/google_categories/');
      return response.data;
    } catch (error) {
      console.error('Error fetching Google Trends categories:', error);
      throw error;
    }
  },

  /**
   * Obtener tendencias de Google para una categoría específica
   * @param {number} categoryId - ID de la categoría
   * @returns {Promise} Resultado de la obtención de tendencias
   */
  fetchTrendsForCategory: async (categoryId) => {
    try {
      const response = await trendsApi.post(`/categories/${categoryId}/fetch_trends/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching trends for category:', error);
      throw error;
    }
  },
};

export default trendsService;
